import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, ShoppingBag, Ticket, Plus, CheckCircle2, XCircle, Clock } from "lucide-react";

const STATUS_BADGE = {
    pending: { label: "Pendiente", icon: Clock, cls: "bg-yellow-200 border-ink" },
    validated: { label: "Validado", icon: CheckCircle2, cls: "bg-mint border-ink" },
    rejected: { label: "Rechazado", icon: XCircle, cls: "bg-red-200 border-ink" },
};

const RAFFLE_STATUS = {
    pending: { label: "Pendiente aprobación", cls: "bg-yellow-200" },
    approved: { label: "Aprobada", cls: "bg-mint" },
    rejected: { label: "Rechazada", cls: "bg-red-200" },
    drawn: { label: "Sorteada", cls: "bg-coral text-white" },
};

export default function Dashboard() {
    const { user } = useAuth();
    const [tab, setTab] = useState("purchases");
    const [purchases, setPurchases] = useState([]);
    const [myRaffles, setMyRaffles] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([api.get("/my/purchases"), api.get("/my/raffles")])
            .then(([p, r]) => {
                setPurchases(p.data);
                setMyRaffles(r.data);
            })
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="dashboard-page">
            <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] font-bold opacity-60 mb-2">Hola, {user?.name}</div>
                    <h1 className="font-display font-black text-5xl uppercase">Mi panel</h1>
                </div>
                <Link to="/crear-rifa" className="brut-btn brut-btn-primary" data-testid="dashboard-create-raffle">
                    <Plus className="w-4 h-4" strokeWidth={3} /> Nueva rifa
                </Link>
            </div>

            <div className="flex border-2 border-ink shadow-brut w-fit mb-6 bg-white">
                <button
                    onClick={() => setTab("purchases")}
                    className={`px-5 py-3 font-bold uppercase tracking-wider text-sm border-r-2 border-ink ${tab === "purchases" ? "bg-mint" : "bg-white"}`}
                    data-testid="tab-purchases"
                >
                    <ShoppingBag className="w-4 h-4 inline mr-1" strokeWidth={3} /> Mis compras ({purchases.length})
                </button>
                <button
                    onClick={() => setTab("raffles")}
                    className={`px-5 py-3 font-bold uppercase tracking-wider text-sm ${tab === "raffles" ? "bg-mint" : "bg-white"}`}
                    data-testid="tab-myraffles"
                >
                    <Ticket className="w-4 h-4 inline mr-1" strokeWidth={3} /> Mis rifas ({myRaffles.length})
                </button>
            </div>

            {loading ? (
                <div className="flex justify-center py-20">
                    <Loader2 className="w-10 h-10 animate-spin" />
                </div>
            ) : tab === "purchases" ? (
                purchases.length === 0 ? (
                    <div className="brut-card p-12 text-center" data-testid="no-purchases">
                        <ShoppingBag className="w-12 h-12 mx-auto mb-4 text-coral" strokeWidth={2} />
                        <p className="font-display font-black text-2xl">Aún no tienes compras</p>
                        <Link to="/rifas" className="brut-btn brut-btn-primary mt-6 inline-flex">Ver rifas</Link>
                    </div>
                ) : (
                    <div className="space-y-4" data-testid="purchases-list">
                        {purchases.map((p) => {
                            const cfg = STATUS_BADGE[p.status] || STATUS_BADGE.pending;
                            const Icon = cfg.icon;
                            return (
                                <div key={p.id} className="brut-card p-5 grid md:grid-cols-12 gap-4 items-center" data-testid={`purchase-${p.id}`}>
                                    <div className="md:col-span-5">
                                        <div className="text-[10px] uppercase tracking-widest font-bold opacity-60">Rifa</div>
                                        <Link to={`/rifas/${p.raffle_id}`} className="font-display font-black text-xl hover:underline">{p.raffle_title}</Link>
                                        <div className="text-sm text-ink/70">{p.raffle_prize}</div>
                                    </div>
                                    <div className="md:col-span-3">
                                        <div className="text-[10px] uppercase tracking-widest font-bold opacity-60">Boletos</div>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {p.ticket_numbers.map((n) => (
                                                <span key={n} className="border-2 border-ink bg-mint px-2 py-0.5 text-xs font-bold">#{n}</span>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="md:col-span-2">
                                        <div className="text-[10px] uppercase tracking-widest font-bold opacity-60">Monto</div>
                                        <div className="font-display font-black text-xl">S/ {p.amount.toFixed(2)}</div>
                                    </div>
                                    <div className="md:col-span-2">
                                        <div className={`border-2 ${cfg.cls} px-2 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-1 justify-center`}>
                                            <Icon className="w-3 h-3" strokeWidth={3} /> {cfg.label}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )
            ) : myRaffles.length === 0 ? (
                <div className="brut-card p-12 text-center" data-testid="no-my-raffles">
                    <Ticket className="w-12 h-12 mx-auto mb-4 text-coral" strokeWidth={2} />
                    <p className="font-display font-black text-2xl">Aún no has creado rifas</p>
                    <Link to="/crear-rifa" className="brut-btn brut-btn-primary mt-6 inline-flex">Crear mi primera rifa</Link>
                </div>
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="my-raffles-list">
                    {myRaffles.map((r) => {
                        const sCfg = RAFFLE_STATUS[r.status] || RAFFLE_STATUS.pending;
                        return (
                            <Link to={`/rifas/${r.id}`} key={r.id} className="brut-card p-5 block hover:-translate-y-1 hover:-translate-x-1 hover:shadow-brut-lg transition-transform" data-testid={`my-raffle-${r.id}`}>
                                <div className={`inline-block border-2 border-ink ${sCfg.cls} px-2 py-1 text-[10px] uppercase tracking-widest font-bold mb-3`}>{sCfg.label}</div>
                                <div className="font-display font-black text-xl">{r.title}</div>
                                <div className="text-sm opacity-70 mb-3">{r.prize}</div>
                                <div className="text-xs uppercase tracking-widest font-bold">
                                    {r.tickets_sold}/{r.total_tickets} vendidos · S/ {r.price_per_ticket.toFixed(2)} c/u
                                </div>
                            </Link>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
